import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const getPasswordStrength = (password) => {
  let score = 0;

  if (password.length >= 8) score += 25;
  if (/[A-Z]/.test(password)) score += 20;
  if (/[a-z]/.test(password)) score += 15;
  if (/\d/.test(password)) score += 20;
  if (/[^A-Za-z0-9]/.test(password)) score += 20;

  if (!password) {
    return { value: 0, label: "", color: "rgba(17,17,17,0.08)" };
  }

  if (score <= 35) {
    return { value: score, label: "Seguridad baja", color: "#e50914" };
  }

  if (score <= 65) {
    return { value: score, label: "Seguridad media", color: "#ff9800" };
  }

  if (score <= 85) {
    return { value: score, label: "Seguridad buena", color: "#22c55e" };
  }

  return { value: 100, label: "Seguridad alta", color: "#16a34a" };
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "18px",
    bgcolor: "#ffffff",
    minHeight: 56,
    fontWeight: 600,
    "& fieldset": {
      borderColor: "rgba(17,17,17,0.14)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(229,9,20,0.35)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#e50914",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
    color: "rgba(17,17,17,0.58)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#e50914",
  },
  "& .MuiFormHelperText-root": {
    minHeight: 18,
    fontWeight: 600,
  },
};

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const passwordStrength = useMemo(
    () => getPasswordStrength(form.password),
    [form.password]
  );

  const passwordMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;

  const passwordMismatch =
    form.confirmPassword.length > 0 && form.password !== form.confirmPassword;

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      general: "",
    }));

    if (banner.message) {
      setBanner({ type: "", message: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.firstName.trim()) newErrors.firstName = "Ingresa tu nombre";
    if (!form.lastName.trim()) newErrors.lastName = "Ingresa tu apellido";

    if (!form.email.trim()) {
      newErrors.email = "Ingresa tu correo electrónico";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Ingresa tu número de teléfono";
    }

    if (!form.password) {
      newErrors.password = "Ingresa una contraseña";
    } else if (form.password.length < 8) {
      newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirma tu contraseña";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/register/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo crear la cuenta";
        setErrors((prev) => ({
          ...prev,
          general: message,
        }));
        setBanner({
          type: "error",
          message,
        });
        return;
      }

      navigate("/verificar-cuenta", {
        state: {
          email: form.email.trim().toLowerCase(),
          message:
            data.message ||
            "Te enviamos un código de verificación a tu correo.",
        },
      });
    } catch (error) {
      const message = "Ocurrió un error al conectar con el servidor";
      setErrors((prev) => ({
        ...prev,
        general: message,
      }));
      setBanner({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Regístrate en NovaXFit | App web para usuarios</title>
        <meta
          name="description"
          content="Crea tu cuenta en NovaXFit y accede a la experiencia digital del gimnasio: perfil de usuario, membresía, código QR, beneficios y herramientas para miembros."
        />
        <link rel="canonical" href="https://novaxfit.com/registro" />
      </Helmet>

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: "#ffffff",
          py: { xs: 4, md: 6 },
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            width: { xs: 280, md: 580 },
            height: { xs: 280, md: 580 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(229,9,20,0.13) 0%, rgba(229,9,20,0.035) 48%, rgba(255,255,255,0) 72%)",
            top: { xs: -150, md: -260 },
            right: { xs: -150, md: -190 },
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: { xs: 280, md: 520 },
            height: { xs: 280, md: 520 },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(17,17,17,0.07) 0%, rgba(17,17,17,0.025) 48%, rgba(255,255,255,0) 72%)",
            bottom: { xs: -160, md: -220 },
            left: { xs: -160, md: -190 },
            pointerEvents: "none",
          },
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            width: { xs: "92%", sm: "88%", md: "82%", xl: "72%" },
            mx: "auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Stack spacing={{ xs: 4, md: 5 }} alignItems="center">
            <Box
              sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                gap: { xs: 3.5, md: 6 },
                alignItems: "center",
              }}
            >
              <Stack
                spacing={{ xs: 2.4, md: 3 }}
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    px: 1.7,
                    py: 0.8,
                    borderRadius: 999,
                    bgcolor: "rgba(229,9,20,0.08)",
                    border: "1px solid rgba(229,9,20,0.16)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#e50914",
                      fontWeight: 900,
                      fontSize: { xs: 12.5, md: 13.5 },
                      letterSpacing: "0.04em",
                    }}
                  >
                    REGISTRO NOVAXFIT
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      color: "#111111",
                      fontWeight: 800,
                      lineHeight: 1.08,
                      letterSpacing: -0.8,
                      textAlign: { xs: "center", md: "left" },
                      fontSize: {
                        xs: "clamp(34px, 9vw, 46px)",
                        md: "3rem",
                      },
                      maxWidth: 600,
                      mx: { xs: "auto", md: 0 },
                      mb: 1.5,
                    }}
                  >
                    Crea tu cuenta y empieza con NovaXFit
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(17,17,17,0.72)",
                      lineHeight: 1.75,
                      fontSize: { xs: 16, md: 17 },
                      fontWeight: 500,
                      textAlign: { xs: "center", md: "left" },
                      maxWidth: 620,
                      mx: { xs: "auto", md: 0 },
                    }}
                  >
                    Completa el registro y revisa la guía rápida para conocer
                    los pasos de acceso e inicio de sesión.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 1.5,
                    width: "100%",
                    maxWidth: 680,
                  }}
                >
                  {[
                    {
                      icon: <TouchAppRoundedIcon />,
                      title: "Regístrate",
                      text: "Ingresa tus datos.",
                    },
                    {
                      icon: <MailOutlineRoundedIcon />,
                      title: "Correo",
                      text: "Recibe tu código.",
                    },
                    {
                      icon: <VerifiedUserRoundedIcon />,
                      title: "Verifica",
                      text: "Activa tu cuenta.",
                    },
                  ].map((item) => (
                    <Box
                      key={item.title}
                      sx={{
                        borderRadius: "22px",
                        border: "1px solid rgba(17,17,17,0.08)",
                        bgcolor: "rgba(255,255,255,0.88)",
                        p: { xs: 1.7, md: 1.9 },
                        display: "flex",
                        gap: 1.3,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: "16px",
                          bgcolor: "#111111",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          "& svg": {
                            fontSize: 25,
                          },
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            color: "#111111",
                            fontWeight: 900,
                            fontSize: 15,
                            lineHeight: 1.15,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: "rgba(17,17,17,0.58)",
                            fontWeight: 650,
                            fontSize: 13,
                            lineHeight: 1.35,
                            mt: 0.3,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Stack>

              <Box
                sx={{
                  borderRadius: { xs: "28px", md: "34px" },
                  border: "1px solid rgba(17,17,17,0.08)",
                  bgcolor: "#ffffff",
                  boxShadow: "0 28px 80px rgba(0,0,0,0.08)",
                  p: { xs: 2, md: 2.3 },
                  width: "100%",
                  maxWidth: { xs: 390, md: 360 },
                  mx: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 1.8,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "#111111",
                        fontWeight: 950,
                        fontSize: { xs: 22, md: 24 },
                        lineHeight: 1.1,
                      }}
                    >
                      Guía rápida
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(17,17,17,0.6)",
                        fontWeight: 700,
                        fontSize: 14,
                        mt: 0.5,
                      }}
                    >
                      Mira el proceso paso a paso
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "18px",
                      bgcolor: "rgba(229,9,20,0.1)",
                      color: "#e50914",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <PlayCircleRoundedIcon sx={{ fontSize: 32 }} />
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 320,
                    mx: "auto",
                    aspectRatio: "360 / 639",
                    borderRadius: "26px",
                    overflow: "hidden",
                    bgcolor: "#000000",
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Box
                    component="iframe"
                    src="https://www.youtube.com/embed/NRETFtWKiDE"
                    title="Registro e inicio de sesión en NovaXfit"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    sx={{
                      width: "100%",
                      height: "100%",
                      border: 0,
                      display: "block",
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                maxWidth: 920,
                borderRadius: { xs: "28px", md: "36px" },
                border: "1px solid rgba(17,17,17,0.08)",
                bgcolor: "#ffffff",
                boxShadow: "0 28px 80px rgba(0,0,0,0.08)",
                p: { xs: 2.4, sm: 3, md: 4.5 },
              }}
            >
              <Stack spacing={2.4}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 0.5,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        color: "#111111",
                        fontWeight: 950,
                        fontSize: { xs: 27, md: 34 },
                        letterSpacing: -0.7,
                        lineHeight: 1.1,
                      }}
                    >
                      Datos de registro
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(17,17,17,0.62)",
                        fontWeight: 650,
                        fontSize: { xs: 15, md: 16 },
                        lineHeight: 1.6,
                        mt: 1,
                      }}
                    >
                      Completa tus datos para crear tu cuenta.
                    </Typography>
                  </Box>
                </Box>

                {banner.message && (
                  <Alert severity={banner.type} sx={{ borderRadius: "18px" }}>
                    {banner.message}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: { xs: 1.2, sm: 1.8 },
                  }}
                >
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={form.firstName}
                    onChange={handleChange("firstName")}
                    error={Boolean(errors.firstName)}
                    helperText={errors.firstName || " "}
                    variant="outlined"
                    sx={fieldSx}
                  />

                  <TextField
                    fullWidth
                    label="Apellido"
                    value={form.lastName}
                    onChange={handleChange("lastName")}
                    error={Boolean(errors.lastName)}
                    helperText={errors.lastName || " "}
                    variant="outlined"
                    sx={fieldSx}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: { xs: 1.2, sm: 1.8 },
                  }}
                >
                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    error={Boolean(errors.email)}
                    helperText={errors.email || " "}
                    variant="outlined"
                    sx={fieldSx}
                  />

                  <TextField
                    fullWidth
                    label="Número de teléfono"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone || " "}
                    variant="outlined"
                    sx={fieldSx}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: { xs: 1.2, sm: 1.8 },
                    alignItems: "start",
                  }}
                >
                  <Box>
                    <TextField
                      fullWidth
                      label="Contraseña"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={handleChange("password")}
                      error={Boolean(errors.password)}
                      helperText={errors.password || " "}
                      variant="outlined"
                      sx={fieldSx}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              aria-label={
                                showPassword
                                  ? "Ocultar contraseña"
                                  : "Mostrar contraseña"
                              }
                              onClick={() => setShowPassword((prev) => !prev)}
                              sx={{ color: "#111111" }}
                            >
                              {showPassword ? (
                                <VisibilityOffRoundedIcon />
                              ) : (
                                <VisibilityRoundedIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box sx={{ mt: -0.3 }}>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength.value}
                        sx={{
                          height: 10,
                          borderRadius: 999,
                          bgcolor: "rgba(17,17,17,0.08)",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            backgroundColor: passwordStrength.color,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          mt: 1,
                          fontSize: 14,
                          fontWeight: 850,
                          color:
                            passwordStrength.color === "rgba(17,17,17,0.08)"
                              ? "rgba(17,17,17,0.45)"
                              : passwordStrength.color,
                        }}
                      >
                        {passwordStrength.label ||
                          "La contraseña debe ser segura"}
                      </Typography>
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirmPassword}
                    onChange={handleChange("confirmPassword")}
                    error={Boolean(errors.confirmPassword) || passwordMismatch}
                    helperText={
                      errors.confirmPassword
                        ? errors.confirmPassword
                        : passwordMismatch
                        ? "Las contraseñas no coinciden"
                        : passwordMatch
                        ? "Las contraseñas coinciden"
                        : " "
                    }
                    FormHelperTextProps={{
                      sx: {
                        color:
                          errors.confirmPassword || passwordMismatch
                            ? "#e50914"
                            : passwordMatch
                            ? "#16a34a"
                            : "transparent",
                        fontWeight: 750,
                      },
                    }}
                    variant="outlined"
                    sx={fieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label={
                              showConfirmPassword
                                ? "Ocultar confirmación de contraseña"
                                : "Mostrar confirmación de contraseña"
                            }
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                            sx={{ color: "#111111" }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOffRoundedIcon />
                            ) : (
                              <VisibilityRoundedIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    pt: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      color: "rgba(17,17,17,0.7)",
                      fontSize: 15.5,
                      fontWeight: 650,
                      order: { xs: 2, sm: 1 },
                    }}
                  >
                    ¿Ya tienes una cuenta?{" "}
                    <Link
                      component={RouterLink}
                      to="/login"
                      underline="none"
                      sx={{
                        color: "#e50914",
                        fontWeight: 950,
                        "&:hover": {
                          color: "#b30010",
                        },
                      }}
                    >
                      Iniciar sesión
                    </Link>
                  </Typography>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      order: { xs: 1, sm: 2 },
                      width: { xs: "100%", sm: 260 },
                      textTransform: "none",
                      fontWeight: 950,
                      borderRadius: "18px",
                      py: 1.45,
                      minHeight: 56,
                      fontSize: "1.03rem",
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg, #68000a 0%, #a60817 36%, #e50914 64%, #211111 100%)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      "&:hover": {
                        filter: "brightness(1.05)",
                      },
                      "&.Mui-disabled": {
                        color: "#ffffff",
                        opacity: 0.75,
                      },
                    }}
                  >
                    {loading ? "Creando cuenta..." : "Crear cuenta"}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
}